import { HttpClient } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { of } from 'rxjs';
import { QueryResult } from '../../pages/home/components/license/types/licenseTypes';
import { Company } from '../../types/company';
import { LicenseService } from './license.service';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private _companies: WritableSignal<Company[]> = signal<Company[]>([]);
  public companies$ = this._companies.asReadonly();

  constructor(
    private _http: HttpClient,
    private _licenseService: LicenseService
  ) {}

  public fetchCompanies() {
    this._http.get('company/all/').subscribe({
      next: async (res: any) => {
        this._companies.set(res);
        // Fetch settings for all companies that have licenses
        if (res.length > 0) {
          const companiesWithLicenses = res.filter((company: any) => 
            company.licenses && company.licenses.length > 0
          );
          
          // Fetch settings for all licenses across all companies
          const licensePromises: Promise<any>[] = [];
          companiesWithLicenses.forEach((company: any) => {
            company.licenses.forEach((license: any) => {
              licensePromises.push(
                this._licenseService.fetchLicenseSettings(license.id),
                this._licenseService.fetchLicenseVersion(license.id)
              );
            });
          });
          
          if (licensePromises.length > 0) {
            await Promise.all(licensePromises);
          }
        }
      },
      error: (error: any) => {
        console.error(error);
      },
    });
  }

  public createCompany(company: any, logo: File | null = null) {
    return new Promise<QueryResult>((resolve) => {
      const formData = new FormData();
      formData.append('name', company.name);
      if (logo) formData.append('logo', logo);
      this._http.post('company/create', formData).subscribe({
        next: (res: any) => {
          this.fetchCompanies();
          resolve(QueryResult.ok);
        },
        error: (error: any) => {
          resolve(QueryResult.error);
          return of(error);
        },
      });
    });
  }

  public editCompany(
    companyForm: any,
    companyId: string,
    logo: File | null = null
  ) {
    return new Promise<QueryResult>((resolve) => {
      const formData = new FormData();
      formData.append('name', companyForm.value.name);
      
      if (logo) {
        formData.append('logo', logo);
      }
      

      
      this._http.put(`company/${companyId}`, formData).subscribe({
        next: (res: any) => {
          this.fetchCompanies();
          resolve(QueryResult.ok);
        },
        error: (error: any) => {
          resolve(QueryResult.error);
          return of(error);
        },
      });
    });
  }

  public deleteCompanyLogo(companyId: string) {
    return new Promise<QueryResult>((resolve) => {
      this._http.delete(`company/${companyId}/logo`).subscribe({
        next: () => {
          this.fetchCompanies();
          resolve(QueryResult.ok);
        },
        error: (error: any) => {
          resolve(QueryResult.error);
        },
      });
    });
  }

  public deleteCompany(companyId: string) {
    return new Promise<QueryResult>((resolve) => {
      this._http.delete('company/' + companyId).subscribe({
        next: () => {
          resolve(QueryResult.ok);
        },
        error: (error: any) => {
          resolve(QueryResult.error);
        },
      });
    });
  }

  private _uploadLogo(file: File, companyId: string) {
    const formData = new FormData();
    formData.append('file', file);
    return this._http.post('company/logo/' + companyId, formData);
  }
}
